import { isAfter, isBefore } from "date-fns";
import * as Yup from "yup";
import type { TTranslate } from "../../core/types";

export const noCommaTest = (t: TTranslate) => ({
  name: "no-comma",
  message: t("companyID_cannot_contain_comma"),
  test: (value) => !value || !value.includes(","),
});

export interface ISchemaOverrides {
  omit?: string[];
  optional?: string[];
  required?: string[];
}

const stripRequired = (schema: any) =>
  typeof schema?.optional === "function"
    ? schema.optional().nullable()
    : schema;

const buildObject = <T extends Record<string, any>>(
  rules: T,
  t: TTranslate,
  overrides?: ISchemaOverrides,
) => {
  const omit = new Set(overrides?.omit ?? []);
  const optional = new Set(overrides?.optional ?? []);
  const required = overrides?.required ?? [];

  const entries: [string, any][] = [];

  for (const [key, rule] of Object.entries(rules) as [string, any][]) {
    if (omit.has(key)) continue;
    entries.push([key, optional.has(key) ? stripRequired(rule) : rule]);
  }

  for (const key of required) {
    if (omit.has(key)) continue;
    entries.push([key, Yup.string().required(t(`${key}_is_required`))]);
  }

  return Yup.object(Object.fromEntries(entries));
};

export const customerSchema = (t: TTranslate, overrides?: ISchemaOverrides) =>
  buildObject(
    {
      customerCompanyName: Yup.string().required(
        t("customerCompanyName_is_required"),
      ),
      customerCompanyID: Yup.string()
        .nullable()
        .required(t("customerCompanyID_is_required"))
        .test(noCommaTest(t)),
      customerCompanyStreet: Yup.string().nullable(),
      customerCompanyStreetNumber: Yup.string().nullable(),
      customerCompanyLocality: Yup.string().nullable(),
      customerCompanyRegion: Yup.string().nullable(),
      customerCompanyZipCode: Yup.string().nullable(),
      customerCountry: Yup.string()
        .required(t("customerCountry_is_required"))
        .typeError(t("customerCountry_is_required")),
      customerEmail: Yup.string()
        .email(t("Email must be a valid email address"))
        .required(t("customerEmail_is_required")),
      currency: Yup.string()
        .required(t("customerCurrency_is_required"))
        .nullable(),
    },
    t,
    overrides,
  );

export const vendorSchema = (t: TTranslate, overrides?: ISchemaOverrides) =>
  buildObject(
    {
      vendorCompanyName: Yup.string().required(
        t("vendorCompanyName_is_required"),
      ),
      vendorCompanyID: Yup.string()
        .nullable()
        .required(t("vendorCompanyID_is_required"))
        .test(noCommaTest(t)),
      vendorCompanyStreet: Yup.string().nullable(),
      vendorCompanyStreetNumber: Yup.string().nullable(),
      vendorCompanyLocality: Yup.string().nullable(),
      vendorCompanyRegion: Yup.string().nullable(),
      vendorCompanyZipCode: Yup.string().nullable(),
      vendorCountry: Yup.string()
        .nullable()
        .required(t("vendorCountry_is_required")),
      vendorEmail: Yup.string().email().required(t("vendorEmail_is_required")),
      logoURL: Yup.string().nullable(),
    },
    t,
    overrides,
  );

export const itemsSchema = (t: TTranslate) =>
  Yup.object({
    totalWithoutTax: Yup.number()
      .required(t("totalWithoutTax_is_required"))
      .transform((v) => (Number.isNaN(v) ? null : v))
      .nullable(),
    items: Yup.array()
      .of(
        Yup.object({
          referenceCode: Yup.string().required(t("referenceCode_is_required")),
          description: Yup.string().required(t("description_is_required")),
          unitPrice: Yup.number()
            .required(t("unitPrice_is_required"))
            .transform((v) => (Number.isNaN(v) ? null : v))
            .typeError(t("unitPrice_is_required")),
          quantity: Yup.number()
            .required(t("quantity_is_required"))
            .transform((v) => (Number.isNaN(v) ? null : v))
            .typeError(t("quantity_is_required")),
        }),
      )
      .min(1, t("atleast_one_item_is_required")),
  });

export const taxesSchema = (t: TTranslate) =>
  Yup.object({
    taxes: Yup.array()
      .defined()
      .of(
        Yup.object({
          baseAmount: Yup.number()
            .transform((v, o) => (o === "" || Number.isNaN(v) ? null : v))
            .nullable()
            .required(t("baseAmount_is_required"))
            .typeError(t("baseAmount_is_required")),
          vatPercentage: Yup.number()
            .required(t("tax_is_required"))
            .typeError(t("tax_is_required")),
          taxType: Yup.string()
            .required(t("taxType_is_required"))
            .typeError(t("taxType_is_required")),
          taxId: Yup.string().nullable().optional(),
        }),
      ),
  });

export const totalsSchema = (t: TTranslate) =>
  Yup.object({
    totalBeforeDiscount: Yup.number().nullable(),
    totalTax: Yup.number().nullable(),
    totalAfterTax: Yup.number().nullable(),
    isFormDiscountEnabled: Yup.boolean().default(false),
    totalDiscount: Yup.number()
      .transform((v, o) => (o === "" || Number.isNaN(v) ? null : v))
      .nullable()
      .when("isFormDiscountEnabled", {
        is: true,
        then: (schema) =>
          schema
            .required(t("discount_is_required"))
            .typeError(t("discount_is_required")),
      }),
  });

export const uploadSchema = () => Yup.object({ uploadedDocument: Yup.mixed() });

export const rectificationSchema = (t: TTranslate) =>
  Yup.object({
    rectificationReason: Yup.string().required(
      t("rectificationReason_is_required"),
    ),
  });

export const spainCorrectionCodeSchema = (t: TTranslate, required: boolean) =>
  Yup.object({
    spainCorrectionCode: required
      ? Yup.string().required(t("spainCorrectionCode_is_required"))
      : Yup.string().nullable(),
  });

export const recurringSchema = () =>
  Yup.object({
    RecurringDocument: Yup.object().shape({
      startDate: Yup.date()
        .nullable()
        .transform((v) => (Number.isNaN(v) ? null : v)),
      endDate: Yup.date()
        .nullable()
        .transform((v) => (Number.isNaN(v) ? null : v)),
      InvoiceDueInDays: Yup.number()
        .nullable()
        .transform((v) => (Number.isNaN(v) ? null : v)),
      RecurringFrequencyType: Yup.string(),
    }),
  });

interface IDocumentInfoOptions {
  t: TTranslate;
  documentClass: string;
  poFieldKey?: "poNumber" | "documentNumber";
  poErrorKey: string;
  poRequired?: boolean;
  dueDateRequired: boolean;
  enforceDateOrder: boolean;
  overrides?: ISchemaOverrides;
}

export const documentInfoSchema = ({
  t,
  documentClass,
  poFieldKey = "poNumber",
  poErrorKey,
  poRequired = false,
  dueDateRequired,
  enforceDateOrder,
  overrides,
}: IDocumentInfoOptions) => {
  const poField = poRequired
    ? Yup.string().required(t(poErrorKey)).nullable()
    : Yup.string().nullable();

  const documentDate = Yup.date()
    .required(t("documentDate_is_required"))
    .typeError(t("add_valid_date"));

  const documentDateWithOrder = enforceDateOrder
    ? documentDate.test(
        "documentDate_less_than_dueDate",
        t("documentDate_should_be_less_than_dueDate"),
        (value, ctx) => isBefore(value!, ctx.parent.dueDate),
      )
    : documentDate;

  const dueDateBase = Yup.date().typeError(t("add_valid_date"));
  const dueDate = dueDateRequired
    ? dueDateBase.required(t("dueDate_is_required"))
    : dueDateBase.nullable();
  const dueDateWithOrder = enforceDateOrder
    ? dueDate.test(
        "dueDate_greater_than_documentDate",
        t("dueDate_should_be_greater_than_documentDate"),
        (value, ctx) => isAfter(value!, ctx.parent.documentDate),
      )
    : dueDate;

  return buildObject(
    {
      documentClass: Yup.string()
        .oneOf([documentClass])
        .required(t("documentClass_is_required")),
      accountClass: Yup.string().required(t("accountClass_is_required")),
      [poFieldKey]: poField,
      documentDate: documentDateWithOrder,
      dueDate: dueDateWithOrder,
      deliveryNote: Yup.string().nullable(),
      comments: Yup.string()
        .max(
          500,
          t("field_name_cannot_exceed_characters", {
            fieldName: t("comments"),
            number: 500,
          }),
        )
        .nullable(),
    },
    t,
    overrides,
  );
};

export const requiredField = (t: TTranslate, name: string) =>
  Yup.string().required(t(`${name}_is_required`));
